import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Heading,
  IconButton,
  Image,
  Text,
  Tooltip,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Spinner,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { FaTrash } from 'react-icons/fa';
import axiosInstance from '../../../services/axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import UpdateTrackModal from './UpdateTrack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import ChangePricePaymentModal from './ChangePricePaymentModal';
import { useSubscription } from '../../../context/SubscriptionContext';
import { useBanner } from '../../../context/BannerContext';
import { FeatureTrackModal, CreditBalanceWidget, PurchaseCreditsModal } from '../../FeaturedTracks';
import { useFeaturedTracks } from '../../../hooks/useFeaturedTracks';

const ArtistTracks = () => {
  const [tracks, setTracks] = useState([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPriceChangeModalOpen, setIsPriceChangeModalOpen] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(false)
  const [paymentDone, setPaymentDone] = useState(false)
  const { userSubscription } = useSubscription()
  const [activeTracks, setActiveTracks] = useState(0);
  const [selectedTrackForFeature, setSelectedTrackForFeature] = useState(null);
  const [trackFeatureStatuses, setTrackFeatureStatuses] = useState({});

  const { showBanner } = useBanner();
  const [loading, setLoading] = useState(false);

  // Featured tracks modals
  const { isOpen: isFeatureModalOpen, onOpen: onFeatureModalOpen, onClose: onFeatureModalClose } = useDisclosure();
  const { isOpen: isPurchaseCreditsOpen, onOpen: onPurchaseCreditsOpen, onClose: onPurchaseCreditsClose } = useDisclosure();

  // Featured tracks hooks
  const { checkFeatureStatus } = useFeaturedTracks();

  const stripePromise = loadStripe("pk_test_51PLQgX2KnKdf1YCEVojH8ER1TVelnSOzUmuNJIJaOadbq1EatVpO2RVndyWTAX0CUmp7fomagqJFkkCQpOJuJB9O00kq1zQolv");

  const handleVerify = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/stripe/create-verification-session');
      if (response.data?.url) {
        window.open(response.data.url, '_blank'); // Open the verification link in a new tab
      }
    } catch (error) {
      console.error('Error generating verification link:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();

  const handlePriceChangeClick = (track) => {
    if (!userSubscription) {
      toast({
        title: 'No active subscription',
        description: 'Subscribe to one of our subscription plans',
        status: 'error',
        isClosable: true,
      });
      navigate("/subscriptions")
      return
    }
    setPaymentModalData(track);
    setIsPriceChangeModalOpen(true);
  };

  const handleFeatureTrackClick = (track) => {
    setSelectedTrackForFeature(track);
    onFeatureModalOpen();
  };

  const handleFeatureSuccess = () => {
    // Refresh track statuses
    fetchTrackFeatureStatuses();
    toast({
      title: 'Track Featured',
      description: 'Your track has been featured successfully!',
      status: 'success',
      isClosable: true,
    });
  };

  const fetchTrackFeatureStatuses = async () => {
    try {
      const statuses = {};
      for (const track of tracks) {
        const status = await checkFeatureStatus(track.track_id);
        statuses[track.track_id] = status;
      }
      setTrackFeatureStatuses(statuses);
    } catch (error) {
      console.error('Error fetching feature statuses:', error);
    }
  };


  const fetchCatalogue = async () => {
    setLoadingTracks(true);
    try {
      // Use linked artist's ID when in switched context, otherwise use own ID
      const userId = auth.isSwitchedContext
        ? auth.user.currentContext.artist_id
        : auth.user.user_id;

      const response = await axiosInstance.get(
        `/users/catalogue/user/${userId}`
      );
      setTracks(response.data.catalogue);
      setActiveTracks(response.data.active_tracks);
    } catch (error) {
      console.error("Error fetching tracks:", error);
    } finally {
      setLoadingTracks(false);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    try {
      // Use linked artist's ID when in switched context, otherwise use own ID
      const userId = auth.isSwitchedContext
        ? auth.user.currentContext.artist_id
        : auth.user.user_id;

      await axiosInstance.delete(`/artists/${userId}/tracks/${trackId}`);
      setTracks(tracks.filter((track) => track.id !== trackId));
      toast({
        title: 'Track Deleted',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting track:', error);
      toast({
        title: 'Error Deleting Track',
        description: 'Failed to delete track. Please try again.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleTrackCreate = () => {
    // Check verification status based on context
    const isVerified = auth.isSwitchedContext
      ? auth.user.switched_context?.isArtistVerified
      : auth.user.account.profile.isArtistVerified;

    if(!isVerified){
      toast({
        title: 'Your account is not verified',
        status: 'error',
        isClosable: true,
      });

      // Check if this is a merchant viewing a linked artist (switched context)
      if (auth.isSwitchedContext) {
        // Show manual verification message for linked artists viewed by merchant
        showBanner("Artist verification is in progress. Documents you uploaded are being reviewed by admin.", "warning", null, 20000);
      } else {
        // Show Stripe verification for independent artists
        showBanner("Your account is not verified.", "error", (
          <Button onClick={handleVerify} isLoading={loading}>
            Verify Now
          </Button>
        ), 20000);
      }
      return;
    }
    if (userSubscription && userSubscription.limit > 0) {
      navigate("/create-music");
    } else {
      toast({
        title: 'subscribe or upgrade your plan',
        status: 'error',
        isClosable: true,
      });
      navigate("/subscriptions")
    }
  }




  useEffect(() => {
    if (auth.user?.user_id) {
      fetchCatalogue();
    }
  }, [paymentDone, auth.user?.user_id]);

  useEffect(() => {
    if (tracks.length > 0) {
      fetchTrackFeatureStatuses();
    }
  }, [tracks]);




  return auth.authLoading ? <Spinner /> :
    (
      <Box>

        <Heading as="h3" size="lg" mb={4} color="dark.900">
          Catalogue
        </Heading>

        {/* Credit Balance Widget */}
        <Box mb={4}>
          <CreditBalanceWidget
            onPurchaseClick={onPurchaseCreditsOpen}
            showDetails={true}
          />
        </Box>

        <Flex justify="space-between" align="center">

          <Button variant="glassy" onClick={handleTrackCreate} mb={4}>
            Create Shares
          </Button>
          <Text color="primary.900" my={3}>{userSubscription && "limit: " + parseInt(userSubscription.limit - activeTracks) + " tracks"}</Text>
        </Flex>
        <Box borderRadius="md" color="primary.100">

          <Accordion allowToggle >
            {tracks.length === 0 && !loadingTracks && <h2>No tracks to show</h2>}
            {loadingTracks ? <Spinner /> : tracks.map((track) => (
              <AccordionItem key={track.trackId} border="none" boxShadow="2xl" p={2} bg="dark.500">

                <AccordionButton>
                  <Grid templateColumns={{ base: "2fr 1fr 1fr", md: "3fr 1fr 1fr" }} templateRows={{ base: "1fr 1fr 1fr 1fr", md: "1fr 1fr" }} alignItems="center" w="100%">
                    <Box display="grid" templateRows="1fr 1fr" templateColumns="1fr 5fr" gridColumn={{ base: "1/4", md: "1/2" }} gridRow={{ base: "1/2", md: "1/2" }} alignItems="center" justifyContent="flex-start">
                      <Image gridColumn="1/2" gridRow="1/3" justifySelf="flex-start" mr={4} src={track.image_url} alt={track.track_name} borderRadius="lg" width={{ base: "3rem", md: "4rem" }} />
                      <Text as={Link} to={`/track/${track.track_id}`} noOfLines={1} gridColumn="2/3" gridRow="1/2" textAlign="left" fontSize="1rem" fontWeight="bold" _hover={{ color: "dark.900" }}>{track.track_name}</Text>
                      <Text noOfLines={1} color="primary.900" gridColumn="2/3" gridRow="2/3" textAlign="left" fontSize="1rem" fontWeight="bold">share price : ${track.price_per_share}</Text>
                    </Box>
                    <Box gridColumn={{ base: "1/4", md: "1/3" }} gridRow={{ base: "2/3", md: "2/3" }}>
                      <Flex alignItems="center" gap={2}>
                        <Text textAlign="left" fontSize="0.8rem" fontWeight="bold">Track Status : <Text as="span" color="orange">{track.track_status}</Text></Text>
                        {trackFeatureStatuses[track.track_id]?.is_featured && (
                          <Badge colorScheme={trackFeatureStatuses[track.track_id].is_scheduled ? "yellow" : "green"}>
                            {trackFeatureStatuses[track.track_id].status === "scheduled" ? "Scheduled" : "Featured"}
                          </Badge>
                        )}
                      </Flex>
                      {/* <Text textAlign="left" fontSize="0.8rem" justifySelf="flex-start" fontWeight="bold">Payment Status : <Text as="span" color={track.payment_status === "paid" ? "green.500" : "red.400"}>{track.payment_status}</Text></Text> */}
                    </Box>
                    {/* <Button size="sm" onClick={(e) => { e.preventDefault(); handleClick(track); }} w="100%" display={track.payment_status === "paid" ? "none" : "block"} bg="green.500" justifySelf="flex-end" gridColumn={{ base: "2/4", md: "3/4" }} gridRow={{ base: "3/4", md: "1/2" }}>Pay</Button> */}
                    <Flex gap={2} justifySelf="flex-end" gridColumn={{ base: "2/4", md: "3/4" }} gridRow={{ base: "3/4", md: "1/2" }} w="100%">
                      <Button size="sm" onClick={(e) => { e.preventDefault(); handlePriceChangeClick(track); }} flex={1}>Change Price</Button>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        onClick={(e) => { e.preventDefault(); handleFeatureTrackClick(track); }}
                        flex={1}
                        isDisabled={trackFeatureStatuses[track.track_id]?.is_featured}
                      >
                        {trackFeatureStatuses[track.track_id]?.is_featured ? 'Featured' : 'Feature Track'}
                      </Button>
                    </Flex>
                    <Box justifySelf="flex-start" display="flex" gridColumn={{ base: "1/2", md: "3/4" }} gridRow={{ base: "3/4", md: "2/3" }} alignItems="center" justifyContent="center">
                      <UpdateTrackModal track={track} isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} />
                      <Tooltip label="Delete" fontSize="md">
                        <IconButton
                          icon={<FaTrash />}
                          colorScheme="red"
                          size="sm"
                          onClick={() => handleDeleteTrack(track.id)}
                          aria-label={`Delete ${track.track_name}`}
                        />
                      </Tooltip>
                    </Box>
                    <Box justifySelf="flex-end" alignSelf="center" gridColumn={{ base: "3/4", md: "3/4" }} gridRow={{ base: "2/3", md: "2/3" }}>

                      <AccordionIcon />
                    </Box>
                  </Grid>
                </AccordionButton>

                <AccordionPanel pb={4} color="primary.100">
                  <Flex
                    direction="column"
                  >
                    <Text>available shares : {track.available_shares}</Text>
                    {/* <Text>Price Per Share : ${track.price_per_share}</Text> */}

                  </Flex>

                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>


        {/* {isPaymentModalOpen && (


          <Elements stripe={stripePromise}>


            <PaymentModal
              isOpen={isPaymentModalOpen}
              onClose={() => setIsPaymentModalOpen(false)}
              onPaymentSuccess={() => { setIsPaymentModalOpen(false); setPaymentDone(true); }}
              trackDetails={paymentModalData}
            />
          </Elements>
        )

        } */}
        {isPriceChangeModalOpen && (


          <Elements stripe={stripePromise}>
            <ChangePricePaymentModal
              isOpen={isPriceChangeModalOpen}
              onClose={() => setIsPriceChangeModalOpen(false)}
              onPaymentSuccess={() => { setIsPriceChangeModalOpen(false); setPaymentDone(true); fetchCatalogue(); }}
              trackDetails={paymentModalData}
            />
          </Elements>
        )
        }

        {/* Feature Track Modal */}
        {selectedTrackForFeature && (
          <FeatureTrackModal
            isOpen={isFeatureModalOpen}
            onClose={onFeatureModalClose}
            track={selectedTrackForFeature}
            onSuccess={handleFeatureSuccess}
          />
        )}

        {/* Purchase Credits Modal */}
        <PurchaseCreditsModal
          isOpen={isPurchaseCreditsOpen}
          onClose={onPurchaseCreditsClose}
        />
      </Box>
    );


};

export default ArtistTracks;

